import React, { useMemo } from 'react'
import {
  Sun,
  LucideIcon
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useGlobalTasks, Tab } from '../../../hooks/multiWindow'
import { DormantIcon, DormantOpenIcon } from '../../../components/icons/DormantIcon'
import { GoalsIcon } from '../../../components/icons/GoalsIcon'
import { UpcomingIcon, UpcomingOpenIcon } from '../../../components/icons/UpcomingIcon'
import { QuickNotesFilledIcon, QuickNotesEmptyIcon, QuickNotesFilledOpenIcon, QuickNotesEmptyOpenIcon } from '../../../components/icons'
import { useQuickNotesHasEntries } from '../../../hooks/useQuickNotesHasEntries'
import { RecurringIcon } from '../../../components/icons/RecurringIcon'
import { SproutIcon } from '../../../components/icons/SproutIcon'
import { SidebarSection } from './SidebarSection'
import { SidebarItem } from './SidebarItem'
import { ProjectsSection } from './ProjectsSection'
import { useSetting } from '../../../lib/db/hooks'

interface PersonalSidebarContentProps {
  activeTab?: Tab
  openTab: (tab: Tab) => void
  navigateActiveTab: (updates: { path: string; title: string; icon?: LucideIcon }) => void
  tabs?: Tab[]
  onCloseTabByPath?: (path: string) => void
  onUnpinByPath?: (path: string) => void
  onPinByPath?: (path: string) => void
  openPreviewTab?: (tab: Omit<Tab, 'id'> & { id?: string }) => void
  promotePreviewBySpace?: (lifeSpace: string) => void
}

export const PersonalSidebarContent: React.FC<PersonalSidebarContentProps> = ({ activeTab, openTab, navigateActiveTab, tabs = [], onCloseTabByPath, onUnpinByPath, onPinByPath, openPreviewTab, promotePreviewBySpace }) => {
  const { t } = useTranslation()
  const { tasks } = useGlobalTasks()
  const quickNotesHasEntries = useQuickNotesHasEntries('personal')
  const quickNotesTabOpen = tabs.some(tab => tab.path === '/home/personal/quick-notes')
  const QuickNotesIcon = quickNotesHasEntries
    ? (quickNotesTabOpen ? QuickNotesFilledOpenIcon : QuickNotesFilledIcon)
    : (quickNotesTabOpen ? QuickNotesEmptyOpenIcon : QuickNotesEmptyIcon)
  const QuickNotesTabIcon = quickNotesHasEntries ? QuickNotesFilledIcon : QuickNotesEmptyIcon
  const { value: tutorialVisible } = useSetting<boolean>('tutorialProjectVisible', true)

  // Counts — ruhende Aufgaben (pausedAt != null) werden in Mein Tag /
  // Demnächst nicht mitgezählt; sie erscheinen nur in Ruhend. Memoized
  // because the sidebar re-renders on every tab switch and `tasks` is the
  // only input — re-filtering twice on each switch was measurable with
  // >100 tasks.
  const { todayCount, upcomingCount } = useMemo(() => {
    const todayStr = new Date().toDateString()
    const todayMs = new Date().setHours(0, 0, 0, 0)
    let today = 0
    let upcoming = 0
    for (const t of tasks) {
      if (t.pausedAt != null) continue
      if (!t.dueDate) continue
      const due = new Date(t.dueDate)
      if (due.toDateString() === todayStr) {
        today++
        continue
      }
      const dayDiff = Math.floor((due.getTime() - todayMs) / 86_400_000)
      if (dayDiff > 0 && dayDiff <= 7) upcoming++
    }
    return { todayCount: today, upcomingCount: upcoming }
  }, [tasks])

  const handleOpenTab = (e: React.MouseEvent, id: string, path: string, title: string, icon?: LucideIcon) => {
    if (e.metaKey || e.ctrlKey) {
      openTab({
        id: `${id}-${Date.now()}`,
        path,
        title,
        icon,
        lifeSpace: 'personal',
        isDirty: false,
        isPinned: false
      })
    } else if (openPreviewTab) {
      openPreviewTab({ path, title, icon, lifeSpace: 'personal' })
    } else {
      navigateActiveTab({ path, title, icon })
    }
  }

  const handleDoubleClick = (path: string) => {
    if (previewPaths.has(path)) {
      promotePreviewBySpace?.('personal')
    } else if (!openPaths.has(path)) {
      promotePreviewBySpace?.('personal')
    }
  }

  const handlePromote = promotePreviewBySpace ? () => promotePreviewBySpace('personal') : undefined

  // Path-indexed sets derived from `tabs`. Same memo reason as above —
  // sidebar rerenders often and `tabs` is the only dep.
  const { openPaths, previewPaths, pinnedPaths } = useMemo(() => {
    const open = new Set<string>()
    const preview = new Set<string>()
    const pinned = new Set<string>()
    // A path counts as pinned only when no non-pinned tab also has it.
    const hasUnpinned = new Set<string>()
    for (const tab of tabs) {
      open.add(tab.path)
      if (tab.isPreview) preview.add(tab.path)
      if (tab.isPinned) pinned.add(tab.path)
      else hasUnpinned.add(tab.path)
    }
    for (const path of hasUnpinned) pinned.delete(path)
    return { openPaths: open, previewPaths: preview, pinnedPaths: pinned }
  }, [tabs])
  const DormantSidebarIcon = openPaths.has('/home/personal/dormant') ? DormantOpenIcon : DormantIcon
  const UpcomingSidebarIcon = openPaths.has('/home/personal/upcoming') ? UpcomingOpenIcon : UpcomingIcon

  return (
    <>
      <div className="standard-sidebar__quick-notes">
        <SidebarItem
          icon={QuickNotesIcon as unknown as LucideIcon}
          label={t('sidebar.quickNotes')}
          path="/home/personal/quick-notes"
          isActive={activeTab?.path === '/home/personal/quick-notes'}
          hasOpenTab={openPaths.has('/home/personal/quick-notes')}
          isPreview={previewPaths.has('/home/personal/quick-notes')}
          isPinned={pinnedPaths.has('/home/personal/quick-notes')}
          onUnpin={onUnpinByPath ? () => onUnpinByPath('/home/personal/quick-notes') : undefined}
          onPinTab={onPinByPath ? () => onPinByPath('/home/personal/quick-notes') : undefined}
          onCloseTab={onCloseTabByPath ? () => onCloseTabByPath('/home/personal/quick-notes') : undefined}
          onOpenNewTab={() => openTab({ id: `personal-quick-notes-${Date.now()}`, path: '/home/personal/quick-notes', title: t('sidebar.quickNotes'), icon: QuickNotesTabIcon as unknown as LucideIcon, lifeSpace: 'personal', isDirty: false, isPinned: false })}
          onClick={(e) => handleOpenTab(e, 'personal-quick-notes', '/home/personal/quick-notes', t('sidebar.quickNotes'), QuickNotesTabIcon as unknown as LucideIcon)}
          onDoubleClick={() => handleDoubleClick('/home/personal/quick-notes')}
          onPromote={handlePromote}
        />
      </div>
      <SidebarSection
        title={t('sidebar.quickOverview')}
        defaultExpanded
      >
        <SidebarItem
          icon={Sun}
          label={t('sidebar.myDay')}
          badge={todayCount}
          path="/home/personal/today"
          isActive={activeTab?.path === '/home/personal/today'}
          hasOpenTab={openPaths.has('/home/personal/today')}
          isPreview={previewPaths.has('/home/personal/today')}
          isPinned={pinnedPaths.has('/home/personal/today')}
          onUnpin={onUnpinByPath ? () => onUnpinByPath('/home/personal/today') : undefined}
          onPinTab={onPinByPath ? () => onPinByPath('/home/personal/today') : undefined}
          onCloseTab={onCloseTabByPath ? () => onCloseTabByPath('/home/personal/today') : undefined}
          onOpenNewTab={() => openTab({ id: `personal-today-${Date.now()}`, path: '/home/personal/today', title: t('sidebar.myDay'), icon: Sun, lifeSpace: 'personal', isDirty: false, isPinned: false })}
          onClick={(e) => handleOpenTab(e, 'personal-today', '/home/personal/today', t('sidebar.myDay'), Sun)}
          onDoubleClick={() => handleDoubleClick('/home/personal/today')}
          onPromote={handlePromote}
        />
        <SidebarItem
          icon={UpcomingSidebarIcon}
          label={t('sidebar.upcoming')}
          badge={upcomingCount}
          path="/home/personal/upcoming"
          isActive={activeTab?.path === '/home/personal/upcoming'}
          hasOpenTab={openPaths.has('/home/personal/upcoming')}
          isPreview={previewPaths.has('/home/personal/upcoming')}
          isPinned={pinnedPaths.has('/home/personal/upcoming')}
          onUnpin={onUnpinByPath ? () => onUnpinByPath('/home/personal/upcoming') : undefined}
          onPinTab={onPinByPath ? () => onPinByPath('/home/personal/upcoming') : undefined}
          onCloseTab={onCloseTabByPath ? () => onCloseTabByPath('/home/personal/upcoming') : undefined}
          onOpenNewTab={() => openTab({ id: `personal-upcoming-${Date.now()}`, path: '/home/personal/upcoming', title: t('sidebar.upcoming'), icon: UpcomingIcon as unknown as LucideIcon, lifeSpace: 'personal', isDirty: false, isPinned: false })}
          onClick={(e) => handleOpenTab(e, 'personal-upcoming', '/home/personal/upcoming', t('sidebar.upcoming'), UpcomingIcon as unknown as LucideIcon)}
          onDoubleClick={() => handleDoubleClick('/home/personal/upcoming')}
          onPromote={handlePromote}
        />
        <SidebarItem
          icon={DormantSidebarIcon as LucideIcon}
          label={t('sidebar.dormant')}
          path="/home/personal/dormant"
          isActive={activeTab?.path === '/home/personal/dormant'}
          hasOpenTab={openPaths.has('/home/personal/dormant')}
          isPreview={previewPaths.has('/home/personal/dormant')}
          isPinned={pinnedPaths.has('/home/personal/dormant')}
          onUnpin={onUnpinByPath ? () => onUnpinByPath('/home/personal/dormant') : undefined}
          onPinTab={onPinByPath ? () => onPinByPath('/home/personal/dormant') : undefined}
          onCloseTab={onCloseTabByPath ? () => onCloseTabByPath('/home/personal/dormant') : undefined}
          onOpenNewTab={() => openTab({ id: `personal-dormant-${Date.now()}`, path: '/home/personal/dormant', title: t('sidebar.dormant'), icon: DormantIcon as LucideIcon, lifeSpace: 'personal', isDirty: false, isPinned: false })}
          onClick={(e) => handleOpenTab(e, 'personal-dormant', '/home/personal/dormant', t('sidebar.dormant'), DormantIcon as LucideIcon)}
          onDoubleClick={() => handleDoubleClick('/home/personal/dormant')}
          onPromote={handlePromote}
        />
      </SidebarSection>

      <SidebarSection
        title={t('sidebar.personalSection')}
        defaultExpanded
      >
        <SidebarItem
          icon={SproutIcon as unknown as LucideIcon}
          label={t('sidebar.habits')}
          path="/home/personal/habits"
          isActive={activeTab?.path === '/home/personal/habits'}
          hasOpenTab={openPaths.has('/home/personal/habits')}
          isPreview={previewPaths.has('/home/personal/habits')}
          isPinned={pinnedPaths.has('/home/personal/habits')}
          onUnpin={onUnpinByPath ? () => onUnpinByPath('/home/personal/habits') : undefined}
          onPinTab={onPinByPath ? () => onPinByPath('/home/personal/habits') : undefined}
          onCloseTab={onCloseTabByPath ? () => onCloseTabByPath('/home/personal/habits') : undefined}
          onOpenNewTab={() => openTab({ id: `personal-habits-${Date.now()}`, path: '/home/personal/habits', title: t('sidebar.habits'), icon: SproutIcon as unknown as LucideIcon, lifeSpace: 'personal', isDirty: false, isPinned: false })}
          onClick={(e) => handleOpenTab(e, 'personal-habits', '/home/personal/habits', t('sidebar.habits'), SproutIcon as unknown as LucideIcon)}
          onDoubleClick={() => handleDoubleClick('/home/personal/habits')}
          onPromote={handlePromote}
        />
        <SidebarItem
          icon={RecurringIcon as unknown as LucideIcon}
          label={t('sidebar.recurring')}
          path="/home/personal/recurring"
          isActive={activeTab?.path === '/home/personal/recurring'}
          hasOpenTab={openPaths.has('/home/personal/recurring')}
          isPreview={previewPaths.has('/home/personal/recurring')}
          isPinned={pinnedPaths.has('/home/personal/recurring')}
          onUnpin={onUnpinByPath ? () => onUnpinByPath('/home/personal/recurring') : undefined}
          onPinTab={onPinByPath ? () => onPinByPath('/home/personal/recurring') : undefined}
          onCloseTab={onCloseTabByPath ? () => onCloseTabByPath('/home/personal/recurring') : undefined}
          onOpenNewTab={() => openTab({ id: `personal-recurring-${Date.now()}`, path: '/home/personal/recurring', title: t('sidebar.recurring'), icon: RecurringIcon as unknown as LucideIcon, lifeSpace: 'personal', isDirty: false, isPinned: false })}
          onClick={(e) => handleOpenTab(e, 'personal-recurring', '/home/personal/recurring', t('sidebar.recurring'), RecurringIcon as unknown as LucideIcon)}
          onDoubleClick={() => handleDoubleClick('/home/personal/recurring')}
          onPromote={handlePromote}
        />
        <SidebarItem
          icon={GoalsIcon}
          label={t('sidebar.goals')}
          path="/home/personal/goals"
          isActive={activeTab?.path === '/home/personal/goals'}
          hasOpenTab={openPaths.has('/home/personal/goals')}
          isPreview={previewPaths.has('/home/personal/goals')}
          isPinned={pinnedPaths.has('/home/personal/goals')}
          onUnpin={onUnpinByPath ? () => onUnpinByPath('/home/personal/goals') : undefined}
          onPinTab={onPinByPath ? () => onPinByPath('/home/personal/goals') : undefined}
          onCloseTab={onCloseTabByPath ? () => onCloseTabByPath('/home/personal/goals') : undefined}
          onOpenNewTab={() => openTab({ id: `personal-goals-${Date.now()}`, path: '/home/personal/goals', title: t('sidebar.goals'), icon: GoalsIcon, lifeSpace: 'personal', isDirty: false, isPinned: false })}
          onClick={(e) => handleOpenTab(e, 'personal-goals', '/home/personal/goals', t('sidebar.goals'), GoalsIcon)}
          onDoubleClick={() => handleDoubleClick('/home/personal/goals')}
          onPromote={handlePromote}
        />
      </SidebarSection>

      <ProjectsSection
        lifeSpace="personal"
        activeTab={activeTab}
        tabs={tabs}
        openTab={openTab}
        navigateActiveTab={navigateActiveTab}
        onCloseTabByPath={onCloseTabByPath}
        onUnpinByPath={onUnpinByPath}
        onPinByPath={onPinByPath}
        openPaths={openPaths}
        previewPaths={previewPaths}
        pinnedPaths={pinnedPaths}
        onOpenTab={handleOpenTab}
        onDoubleClick={handleDoubleClick}
        onPromote={handlePromote}
        showTutorial={tutorialVisible}
      />
    </>
  )
}
